import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { catchError, forkJoin, of, take } from 'rxjs';
import { MatDialogComponent } from 'src/app/mat-dialog/mat-dialog.component';
import { MediaCarouselComponent } from 'src/app/media-carousel/media-carousel.component';
import { AppService } from 'src/app/services/app.service';
import { Client, DealerMediaResponse, EndApproval, FileLinkWithThumbnail, Approvalstatus, ApprovalStatusLowerCase, StyleSettingsDealerImage } from 'src/app/services/client.service';
import { TickSnackbarComponent } from 'src/app/tick-snackbar/tick-snackbar.component';

@Component({
  selector: 'app-service-order-auth',
  standalone: true,
  imports: [
    MatExpansionModule,
    MatIconModule,
    MatDialogModule,
    MatTabsModule,
    CommonModule,
    MediaCarouselComponent
  ],
  templateUrl: './service-order-auth.component.html',
  styleUrl: './service-order-auth.component.scss'
})
export class ServiceOrderAuthComponent implements OnInit {
  @Input() apiResponse: any;
  @Output() updateChecklistData = new EventEmitter<void>();
  serviceJobs: any[] = [];
  pendingJobs: any[] = [];
  authorizedJobs: any[] = [];
  declinedJobs: any[] = [];
  filteredJobs: any[] = [];
  token: string = '';
  siteSettings: any;
  dealerImageDetails: StyleSettingsDealerImage | undefined;
  bodyData: EndApproval[] = [];
  locationId: string | undefined;
  authToken: string = '';
  companyId: string | undefined;
  orderId: string | undefined;
  introOutroMediaData!: DealerMediaResponse;
  isWalkthroughEnabled: boolean | undefined = false;
  isLargeScreen: boolean = window.innerWidth > 900;
  pdfBase64Data: string = '';
  isPDFDataAvailable: boolean = true;
  isDeclineDisabled = false;
  isRequestCallbackDisabled = false;
  isAuthorizeDisabled = false;
  showDeclineButton = true;
  showAuthorizeButton = true;
  isAuthorized = false;
  totalPendingPrice: number | undefined;
  totalPendingJobsCount: number = 0;
  selectedTab: any;
  showFooter = false;
  private lastScrollTop = 0;
  mediaItems: FileLinkWithThumbnail[] | undefined;
  private swipeCooldown = false;
  Approvalstatus = Approvalstatus;
  ApprovalstatusLowerCase = ApprovalStatusLowerCase;
  details: any;
  serviceOrderServiceJobs: any = [];
  selectedTabIndex: number = 0;
  dealerImageDisplay: boolean = false;
  currentWindowWidth: number = window.innerWidth;
  noPendingRequest: string = $localize`No pending requests`;
  pendingDescription: string = $localize`There are no pending requests for your vehicle.`;
  noAuthorizedRequest: string = $localize`No authorized requests`;
  authorizedDescription: string = $localize`Go to pending requests to approve jobs for your vehicle.`;
  noDeclinedRequest: string = $localize`No declined requests`;
  declinedDescription: string = $localize`Go to pending requests to decline jobs for your vehicle.`;
  pendingRequestsLabel = $localize`:@@pendingRequests:Pending Requests`;
  partsLabel = $localize`:@@parts:Parts`;
  labourLabel = $localize`:@@labour:Labours`;
  glAccount = $localize`:@@glAccount:G/L Account`;
  ExServices = $localize`:@@Ex.Services:External Service`;
  authorizationSent: string = $localize`Authorization sent`;

  @HostListener('swipeleft', ['$event'])
  onSwipeLeft(event: any) {
    if (this.selectedTabIndex < 3 && !this.swipeCooldown) {
      this.swipeCooldown = true;
      this.selectTab(this.selectedTabIndex + 1);
      setTimeout(() => (this.swipeCooldown = false), 300);
    }
  }

  @HostListener('swiperight', ['$event'])
  onSwipeRight(event: any) {
    if (this.selectedTabIndex > 0 && !this.swipeCooldown) {
      this.swipeCooldown = true;
      this.selectTab(this.selectedTabIndex - 1);
      setTimeout(() => (this.swipeCooldown = false), 300);
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (window.innerWidth > 900) {
      if (scrollTop > this.lastScrollTop) {
        this.showFooter = true;
      }
      this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.currentWindowWidth = (event.target as Window).innerWidth;
    this.hideDealerImage(); 
  }
  

  private hideDealerImage(): void {
    if (this.dealerImageDetails) {
      this.dealerImageDisplay = !this.dealerImageDetails.url && this.currentWindowWidth > 900;
    } else {
      this.dealerImageDisplay = false;
    }
  }
  

  constructor(private appService: AppService, public dialog: MatDialog, private readonly client: Client, private readonly route: ActivatedRoute, private snackBar: MatSnackBar) { }

   ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['filter']) {
        this.selectedTab = params['filter'];
        this.selectedTabIndex = 3;
      }
      this.token = (params['token']);
    });
    this.loadChecklistData();
    if (window.innerWidth <= 900) {
      this.showFooter = true;
    }
  }

  checkScreenSize() {
    this.isLargeScreen = window.innerWidth > 900;
  }

  loadChecklistData() {
    this.appService.serviceOrderResponseData$.pipe(take(1)).subscribe((data) => {
      if (data) {
        this.apiResponse = data;
        this.details = this.apiResponse?.serviceOrder;
        this.companyId = this.apiResponse?.companyId;
        this.orderId = this.details?.id;
        this.locationId = this.details?.locationId;
        this.authToken = this.apiResponse?.token;
        this.processChecklistData();
      } else {
        forkJoin({
          details: this.appService.getServiceOrderDetails(this.token),
          stylesettings: this.appService.getStyleSettings(this.token),
          dealerMedia: this.appService.getDealerMediaResponse(this.token).pipe(
            catchError((error) => {
              return of(null);
            })
          )
        })
          .pipe(take(1))
          .subscribe({
            next: ({ details, stylesettings, dealerMedia }) => {
              this.apiResponse = details;
              this.authToken = this.apiResponse?.token;
              this.companyId = this.apiResponse?.companyId;
              this.orderId = this.apiResponse?.serviceOrder.id;
              this.locationId = this.apiResponse?.serviceOrder.locationId;
              this.siteSettings = stylesettings;
              this.dealerImageDetails = stylesettings?.dealerImage;
              this.hideDealerImage();
              this.introOutroMediaData = dealerMedia;
              this.processChecklistData();
            },
            error: (error) => {
              const errorMessage = error instanceof Error ? error.message : String(error);
              this.appService.handleErrorMessage(errorMessage);
            }
          });
      }
    })
  }

  private processChecklistData() {
    if (this.apiResponse) {
      this.details = this.apiResponse?.serviceOrder;
      this.fetchSchedulingServiceJobs();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['apiResponse']?.currentValue) {
      this.apiResponse = changes['apiResponse'].currentValue;
      this.companyId = this.apiResponse?.companyId;
      this.details = this.apiResponse?.serviceOrder;
      this.locationId = this.details?.locationId;
      this.orderId = this.details?.id;
      this.authToken = this.apiResponse?.token;
      this.processChecklistData();
    }
  }

  fetchSchedulingServiceJobs() {
    if (!this.details?.serviceJobs) return;
    this.serviceJobs = [...this.details.serviceJobs];

    this.serviceOrderServiceJobs = [...this.serviceJobs];
    this.applyFilter();
  }

  applyFilter() {
    switch (this.selectedTab) {
      case 'authorized':
        this.serviceOrderServiceJobs = this.serviceJobs.filter(
          (job: any) => job.approvalStatus === Approvalstatus.Accept
        );
        this.totalPendingJobsCounts();
        this.isDeclineDisabled = true;
        this.isAuthorizeDisabled = true;
        break;
      case 'declined':
        this.serviceOrderServiceJobs = this.serviceJobs.filter(
          (job: any) => job.approvalStatus === Approvalstatus.Reject
        );
        this.totalPendingJobsCounts();
        this.isDeclineDisabled = true;
        this.isAuthorizeDisabled = true;
        break;
      default: 
      this.serviceOrderServiceJobs = this.serviceJobs.filter(
        (job: any) => {
          const status = job.approvalStatus?.trim().toLowerCase() || '';
          return status === '' || status === ApprovalStatusLowerCase.NotProvided;
        }
      );
        this.totalPendingJobsAndPendingPrice();
        this.isDeclineDisabled = false;
        this.isAuthorizeDisabled = false;
    }

  }

  totalPendingJobsAndPendingPrice(){
    this.totalPendingJobsCount = this.serviceOrderServiceJobs.length;

    const pendingPrices = this.serviceOrderServiceJobs.flatMap((job: any) =>
      job.priceSummary?.map((priceSummary: any) =>
        priceSummary?.price?.grossPrice || 0
      ) || []
    );

    this.totalPendingPrice = this.calculateGrossTotal(pendingPrices);
  }

  totalPendingJobsCounts() {
    const pendingJobs = this.serviceJobs.filter(
      (job: any) => {
        const status = job.approvalStatus?.trim().toLowerCase() || '';
        return status === '' || status === ApprovalStatusLowerCase.NotProvided;
      }
    );
    this.totalPendingJobsCount = pendingJobs.length;
  }

  selectTab(index: number) {
    const tabMap = ['pending', 'authorized', 'declined'];
    this.selectedTab = tabMap[index];
    this.selectedTabIndex = index;
    this.applyFilter();
  }

  getServiceOrderReport(token: string) {
    this.appService.getServiceOrderPDFReport(token)?.subscribe((data: string) => {
      this.pdfBase64Data = data;
      this.isPDFDataAvailable = true;
    })
  }

  isObjectEmpty(obj: any): boolean {
    return Object.keys(obj).length === 0;
  }

  get pendingTabLabel(): string {
    return `${this.pendingRequestsLabel} (${this.getPendingRequestsCount()})`;
  }

  priceTypeLabel(type: string | undefined): string {
    switch (type) {
      case 'Items':
        return this.partsLabel;
      case 'Labours':
        return this.labourLabel;
      case 'G/L Accounts':
        return this.glAccount;
      case 'Ex.Services':
        return this.ExServices;  
      default:
        return type || '';
    }
  }
  
  getPendingRequestsCount(): number {
    return (this.totalPendingJobsCount);
  }

  calculateGrossTotal(priceSummary: number[]): number {
    const total = priceSummary.reduce((sum, price) => sum + price, 0);
    const roundedTotal = parseFloat(total.toFixed(2));
    return roundedTotal;
  }

  onButtonGroupClick(buttonName: string, job: any) {
    let context = "Quote";
    if (buttonName === Approvalstatus.Accept) {
      this.approveOrDeclineCall(buttonName, job)
    }
    else {
      const dialogRef = this.dialog.open(MatDialogComponent, {
        data: {
          context: context,
          serviceJob: job?.description,

        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.approveOrDeclineCall(buttonName, job)
        }
      });
    }

  }

  approveOrDeclineCall(buttonName: string, job: any) {
    this.bodyData = [
      <EndApproval>{
        id: String(job.id),
        description: job.description,
        descriptionLong: job.descritpionLong,
        price: job.price,
        approvalStatus: buttonName,
        billToClientName: job.billToCustomerName
      }
    ];
  
    const locationId = this.locationId?.replace(/[{ }]/g, '');
  
    this.client.consentDetails(this.authToken, this.companyId, this.orderId, locationId, this.bodyData)
      .subscribe((response: any) => {
        if (response?.status === 200 && buttonName) {
          const updatedJob = this.serviceJobs?.find((x: any) => x.id === job.id);
          if (updatedJob) {
            updatedJob.approvalStatus = buttonName;
          }
  
          if (buttonName === Approvalstatus.Accept) {
            this.showAuthorizationMessage();
          }
  
          this.applyFilter();
          this.updateChecklistData.emit();
        }
      });
  }
  
  onApproveAllOrDeclineAllClick(buttonName: string) {
    const isDisabled =
      (buttonName === Approvalstatus.Accept && (this.isAuthorizeDisabled || (this.totalPendingJobsCount ?? 0) <= 1)) ||
      (buttonName === Approvalstatus.Reject && (this.isDeclineDisabled || (this.totalPendingJobsCount ?? 0) <= 1));

    if (isDisabled) {
      return;
    }

    const serviceJobs = Array.isArray(this.serviceOrderServiceJobs) ? [...this.serviceOrderServiceJobs] : [];

    const arrayOfMatchedData = serviceJobs.filter(
      (job: any) => job.approvalStatus !== Approvalstatus.Accept && job.approvalStatus !== Approvalstatus.Reject
    );
    const dialogRef = this.dialog.open(MatDialogComponent, {
      data: {
        type: buttonName + 'Disclaimer',
        jobs: (arrayOfMatchedData.length == this.serviceJobs.length) ? 'all' : arrayOfMatchedData.length,
        cost: this.totalPendingPrice
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.approveOrDeclineAllCall(buttonName);
      }
    });
  }


  approveOrDeclineAllCall(buttonName: string) {
    const serviceJobs = Array.isArray(this.serviceOrderServiceJobs) ? [...this.serviceOrderServiceJobs] : [];
    const arrayOfMatchedData = serviceJobs.filter(
      (job: any) => job.approvalStatus !== Approvalstatus.Accept && job.approvalStatus !== Approvalstatus.Reject
    );

    this.bodyData = arrayOfMatchedData.map((job: any) => <EndApproval>{
      id: String(job.id),
      description: job.description,
      descriptionLong: job.descritpionLong,
      price: job.price,
      approvalStatus: buttonName,
      billToClientName: job.billToCustomerName
    });

    const locationId = this.locationId?.replace(/[{ }]/g, '');

    this.client.consentDetails(this.authToken, this.companyId, this.orderId, locationId, this.bodyData).subscribe({
      next: () => {
        arrayOfMatchedData.forEach((job: any) => {
          job.approvalStatus = buttonName;
        });

        this.updateButtonStates(buttonName);
        this.applyFilter();
        this.updateChecklistData.emit();
      },
      error: (error) => {
        this.appService.handleErrorMessage(error);
      }
    });
  }

  updateButtonStates(buttonName: string) {
    if (buttonName === Approvalstatus.Accept) {
      this.isAuthorizeDisabled = true;
      this.isDeclineDisabled = true;
      this.isRequestCallbackDisabled = true;
    } else if (buttonName === Approvalstatus.Reject) {
      this.isDeclineDisabled = true;
      this.isAuthorizeDisabled = true;
      this.isRequestCallbackDisabled = true;
    } else if (buttonName === Approvalstatus.NotProvided) {
      this.isRequestCallbackDisabled = false;
      this.isAuthorizeDisabled = false;
      this.isDeclineDisabled = false;
    }
  }

  getNoJobsMessage(): { title: string; description: string } {
    switch (this.selectedTab) {
      case 'pending':
        return {
          title: this.noPendingRequest,
          description: this.pendingDescription
        };
      case 'authorized':
        return {
          title: this.noAuthorizedRequest,
          description: this.authorizedDescription
        };
      case 'declined':
        return {
          title: this.noDeclinedRequest,
          description: this.declinedDescription
        };
      default:
        return {
          title: 'No jobs available',
          description: `There are no job recorded for your vehicle at the moment.`
        };
    }
  }
  getTotalGrossPrice(priceSummary: any[]): number {
    if (!priceSummary || !Array.isArray(priceSummary)) return 0;
    return priceSummary.reduce((total, item) => total + ((item?.price?.grossPrice || item?.grossPrice) || 0), 0).toFixed(2);
  }

  getTotalDiscount(priceSummary: any[]): number {
    if (!priceSummary || !Array.isArray(priceSummary)) return 0;
    return Number(priceSummary.reduce((total, item) => total + ((item?.price?.discount?.amount || item?.discount?.amount) || 0), 0).toFixed(2));
  }

  getTotalTaxes(priceSummary: any[]): string {
    if (!priceSummary || !Array.isArray(priceSummary)) return '0.00';
    const totalTax = priceSummary.reduce((total, item) => {
      const taxes = item?.price?.taxes || [];
      const taxSum = taxes.reduce((taxTotal: number, tax: any) => taxTotal + (tax?.value || 0), 0);
      return total + taxSum;
    }, 0);
    return totalTax.toFixed(2);
  }
  
  showAuthorizationMessage(): void {
    this.snackBar.openFromComponent(TickSnackbarComponent, {
      data: this.authorizationSent,
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['custom-snackbar']
    });
  }

  hasFindingMedia(checkpoint: any): boolean {
  return checkpoint?.findings?.some((f: any) => f?.media?.length > 0);
}
  getCurrency(priceSummary: any[]): string {
    if (!priceSummary || !Array.isArray(priceSummary) || priceSummary.length === 0) {
      return 'USD';
    }
    
    const priceWithCurrency = priceSummary.find(item => item?.currency || item?.price?.currency);
    return priceWithCurrency?.currency || priceWithCurrency?.price?.currency || 'USD';
  }
}
